import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Plus, X, ChevronDown, UserPlus, Check, Pencil } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Generic Searchable Selector ───────────────────────────────────────────────
export function SearchableSelector({ value, onChange, items, placeholder, apiEndpoint, authHeaders, onCreated, icon: Icon }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [newItem, setNewItem] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const filtered = items.filter(i => !search || i.name?.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectItem = (item) => { onChange(item.name); setIsOpen(false); setSearch(""); };

  const createNew = async () => {
    if (!newItem.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/${apiEndpoint}`, { name: newItem.trim() }, { headers: authHeaders });
      onChange(newItem.trim());
      onCreated?.();
      setNewItem(""); setIsOpen(false);
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button type="button" onClick={() => { setIsOpen(!isOpen); setTimeout(() => inputRef.current?.focus(), 50); }}
        className="w-full flex items-center justify-between border border-gray-200 px-3 py-2.5 text-sm text-left focus:outline-none focus:border-[#D4A843] bg-white">
        <span className="flex items-center gap-2">
          {Icon && <Icon size={14} className="text-gray-400" />}
          {value || <span className="text-gray-400">{placeholder}</span>}
        </span>
        <ChevronDown size={14} className="text-gray-400" />
      </button>
      {isOpen && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 shadow-lg max-h-72 overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <input ref={inputRef} value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Rechercher..."
              className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-[#D4A843]" />
          </div>
          <div className="max-h-40 overflow-y-auto">
            {filtered.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-3">Aucun résultat</p>
            ) : filtered.map(i => (
              <button key={i.id} type="button" onClick={() => selectItem(i)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-50 ${value === i.name ? 'bg-amber-50' : ''}`}>
                {i.name}
              </button>
            ))}
          </div>
          <div className="p-2 border-t border-gray-100 bg-gray-50">
            <div className="flex gap-2">
              <input value={newItem} onChange={e => setNewItem(e.target.value)}
                placeholder="Créer nouveau..."
                onKeyDown={e => e.key === "Enter" && createNew()}
                className="flex-1 border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:border-[#D4A843] bg-white" />
              <button type="button" onClick={createNew} disabled={saving || !newItem.trim()}
                className="px-3 py-1.5 bg-[#D4A843] text-white text-sm font-semibold disabled:opacity-50">
                <Plus size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Client Selector with Search ───────────────────────────────────────────────
export function ClientSelector({ value, onChange, clients, onClientCreated, authHeaders }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const selectedClient = clients.find(c => c.id === value);
  const filtered = clients.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return c.nom?.toLowerCase().includes(s) || c.email?.toLowerCase().includes(s) || c.telephone?.includes(s);
  });

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectClient = (client) => { onChange(client); setIsOpen(false); setSearch(""); };

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <button type="button" onClick={() => { setIsOpen(!isOpen); setTimeout(() => inputRef.current?.focus(), 50); }}
            data-testid="client-selector-btn"
            className="w-full flex items-center justify-between border border-gray-200 px-3 py-2.5 text-sm text-left focus:outline-none focus:border-[#D4A843] bg-white">
            {selectedClient ? (
              <span className="truncate">{selectedClient.nom} — {selectedClient.email}</span>
            ) : (
              <span className="text-gray-400">Sélectionner un client...</span>
            )}
            <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />
          </button>
          {isOpen && (
            <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 shadow-lg max-h-64 overflow-hidden">
              <div className="p-2 border-b border-gray-100">
                <input ref={inputRef} value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Rechercher un client..."
                  data-testid="client-search-input"
                  className="w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-[#D4A843]" />
              </div>
              <div className="max-h-48 overflow-y-auto">
                {filtered.length === 0 ? (
                  <p className="text-center text-gray-400 text-sm py-4">Aucun client trouvé</p>
                ) : filtered.map(c => (
                  <button key={c.id} type="button" onClick={() => selectClient(c)}
                    data-testid={`client-option-${c.id}`}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 border-b border-gray-50 ${value === c.id ? 'bg-amber-50' : ''}`}>
                    <p className="font-medium text-gray-900">{c.nom}</p>
                    <p className="text-xs text-gray-500">{c.email} • {c.telephone}</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <button type="button" onClick={() => setShowCreate(true)} title="Créer un nouveau client"
          data-testid="add-client-btn"
          className="px-3 py-2 border border-[#D4A843] text-[#D4A843] hover:bg-amber-50 transition-colors text-sm font-semibold">
          <UserPlus size={15} />
        </button>
      </div>
      {showCreate && (
        <QuickClientModal authHeaders={authHeaders}
          onClose={() => setShowCreate(false)}
          onCreated={(newClient) => { setShowCreate(false); onClientCreated?.(); selectClient(newClient); }} />
      )}
    </div>
  );
}

// ── Quick Client Creation Modal ───────────────────────────────────────────────
export function QuickClientModal({ authHeaders, onClose, onCreated }) {
  const [form, setForm] = useState({ nom: "", email: "", telephone: "", adresse: "", code_postal: "", ville: "", societe: "", siret: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [addrSuggest, setAddrSuggest] = useState([]);
  const [showSuggest, setShowSuggest] = useState(false);
  const debounceRef = useRef(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Autocomplétion adresse via la Base Adresse Nationale (gratuit, sans clé, couvre La Réunion)
  const onAddrChange = (v) => {
    set("adresse", v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (v.trim().length < 3) { setAddrSuggest([]); setShowSuggest(false); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(v)}&limit=5&autocomplete=1`);
        setAddrSuggest(res.data?.features || []);
        setShowSuggest(true);
      } catch { setAddrSuggest([]); }
    }, 300);
  };

  const pickAddr = (feat) => {
    const p = feat.properties || {};
    setForm(f => ({ ...f, adresse: p.name || f.adresse, code_postal: p.postcode || "", ville: p.city || "" }));
    setShowSuggest(false); setAddrSuggest([]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const res = await axios.post(`${API}/clients`, form, { headers: authHeaders });
      onCreated(res.data);
    } catch (err) { setError(err.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  const inputCls = "w-full border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:border-[#D4A843]";

  return createPortal(
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">Nouveau client</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          {error && <p className="text-red-500 text-sm bg-red-50 p-2">{error}</p>}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Nom *</label>
              <input type="text" value={form.nom} onChange={e => set("nom", e.target.value)} required
                data-testid="quick-client-nom" className={inputCls} />
            </div>
            <div className="col-span-2 relative">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Adresse</label>
              <input type="text" value={form.adresse} onChange={e => onAddrChange(e.target.value)}
                onFocus={() => addrSuggest.length && setShowSuggest(true)} autoComplete="off"
                placeholder="Commencez à taper une adresse..."
                data-testid="quick-client-adresse" className={inputCls} />
              {showSuggest && addrSuggest.length > 0 && (
                <div className="absolute z-[70] left-0 right-0 mt-1 bg-white border border-gray-200 shadow-lg max-h-52 overflow-y-auto">
                  {addrSuggest.map((f, i) => (
                    <button type="button" key={i} onClick={() => pickAddr(f)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-amber-50 border-b border-gray-50">
                      {f.properties?.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Code postal</label>
              <input type="text" value={form.code_postal} onChange={e => set("code_postal", e.target.value)}
                data-testid="quick-client-code_postal" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Ville</label>
              <input type="text" value={form.ville} onChange={e => set("ville", e.target.value)}
                data-testid="quick-client-ville" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Email *</label>
              <input type="email" value={form.email} onChange={e => set("email", e.target.value)} required
                data-testid="quick-client-email" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Téléphone *</label>
              <input type="tel" value={form.telephone} onChange={e => set("telephone", e.target.value)} required
                data-testid="quick-client-telephone" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Société</label>
              <input type="text" value={form.societe} onChange={e => set("societe", e.target.value)}
                data-testid="quick-client-societe" className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">SIRET</label>
              <input type="text" value={form.siret} onChange={e => set("siret", e.target.value)}
                data-testid="quick-client-siret" className={inputCls} />
            </div>
          </div>
          <div className="flex gap-3 pt-2 justify-end">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-200 text-sm font-semibold">Annuler</button>
            <button type="submit" disabled={loading} data-testid="quick-client-save"
              className="px-5 py-2 bg-[#1B2E4B] text-white text-sm font-semibold disabled:opacity-60">{loading ? "..." : "Créer"}</button>
          </div>
        </form>
      </div>
    </div>
  , document.body);
}

// ── Inline Price Editor ───────────────────────────────────────────────────────
export function InlinePriceEdit({ montant, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(montant);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (val === montant) { setEditing(false); return; }
    setSaving(true);
    try { await onSave(parseInt(val) || montant); setEditing(false); }
    catch (e) { alert("Erreur lors de la mise à jour du prix"); }
    finally { setSaving(false); }
  };

  if (editing) {
    return (
      <div className="flex items-center justify-center gap-1 mt-1">
        <input type="number" value={val} onChange={e => setVal(e.target.value)}
          autoFocus onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
          className="w-20 border border-blue-300 px-2 py-1 text-sm text-center font-bold focus:outline-none rounded" />
        <span className="text-sm font-bold">€</span>
        <button onClick={save} disabled={saving} className="p-1 text-green-600 hover:text-green-800" title="Valider">
          <Check size={14} />
        </button>
        <button onClick={() => setEditing(false)} className="p-1 text-gray-400 hover:text-gray-600" title="Annuler">
          <X size={12} />
        </button>
      </div>
    );
  }

  return (
    <button onClick={() => { setVal(montant); setEditing(true); }}
      className="group/price block w-full" title="Cliquer pour modifier le prix">
      <p className="text-lg font-bold text-gray-900 group-hover/price:text-blue-700 transition-colors">
        {montant}€
      </p>
      <p className="text-[10px] text-gray-400 group-hover/price:text-blue-400 flex items-center justify-center gap-1">
        <Pencil size={9} /> modifier
      </p>
    </button>
  );
}
